import './styles.scss';

import React, { useEffect, useState } from 'react';

import { Button, Modal } from 'antd';
import TMIcon from 'tm-icons-library';

import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';
import { useTheme } from '../../context/ThemeProvider';
import { checkForPermissions } from '../../context/userReducer';
import {
  secondsToHms,
  useAppSelector,
  useCountdownTimer,
} from '../../shared/hooks';
import { dynamicItemTitle } from '../../shared/utils';
import {
  AssignmentQuestionCategory,
  IAssignmentSubmission,
  ICourseAssignment,
} from '../../types/assignmentTypes';
import { UAMPermissions } from '../../types/userTypes';
import AssignmentQuestion from './components/AssignmentQuestion/AssignmentQuestion';
import AssignmentReviewSubmit from './components/AssignmentReviewSubmit/AssignmentReviewSubmit';
import {
  AssignmentProvider,
  useAssignmentContext,
} from './utils/AssignmentProvider';

interface CircularProgressBarProps {
  percentage: number;
  size: number;
  strokeWidth: number;
  color: string;
}

const CircularProgressBar: React.FC<CircularProgressBarProps> = ({
  percentage,
  size,
  strokeWidth,
  color,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#e5e5e5"
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={strokeDasharray}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        style={{
          transition: 'stroke-dashoffset 0.3s ease-in-out',
        }}
      />
    </svg>
  );
};

interface Props {
  retake?: boolean;
  isOpen: boolean;
  closeModal: () => void;
  assignment: ICourseAssignment;
  submission: IAssignmentSubmission;
  flow: 'submit' | 'preview' | 'review';
  onProgress: (progress: number, duration: number) => void;
}

const AssignmentBody: React.FC<Props> = ({ closeModal, isOpen }) => {
  const { colors } = useTheme();

  const {
    id: userId,
    type: userType,
    userAccessPermissions,
  } = useAppSelector((state) => state.user);

  const {
    isSaving,
    showReviewAndSubmit,
    setShowReviewAndSubmit,
    flow,
    assignment,
    submission,
    questionsMapByIndex,
    currentIndex,
    setCurrentIndex,
    submitAnswersAndEndAssignment,
    goToPrevOrNextQuestion,
    goToNextUnattemptedQuestion,
    saveQuestionAnswer,
    hasPrevQuestion,
    assignmentToEndAt,
    closeSubmitAssignmentModal,
  } = useAssignmentContext();

  const { secondsLeft, isOver } = useCountdownTimer(assignmentToEndAt);

  const [showExitModal, setShowExitModal] = useState(false);

  const { enableCountdown, countdown, retaking } = assignment || {};

  const totalCountdown = countdown ?? 0;
  const remaining = secondsLeft ?? totalCountdown;
  const elapsed = Math.max(0, totalCountdown - remaining);
  const ongoingCountdownPercentage =
    totalCountdown > 0 ? Math.min(100, (elapsed / totalCountdown) * 100) : 0;

  const { obtainedMarksForQuestions = [] } = submission || {};
  const isLastQuestion =
    obtainedMarksForQuestions.length === 0 ||
    currentIndex === obtainedMarksForQuestions.length - 1;
  const currentQuestion = questionsMapByIndex[currentIndex];

  const hasQuestionAnswer = React.useMemo(() => {
    return (
      (currentQuestion?.type !== AssignmentQuestionCategory.SUBJECTIVE &&
        currentQuestion?.selectedOptions?.length) ||
      (currentQuestion?.type === AssignmentQuestionCategory.SUBJECTIVE &&
        currentQuestion?.subjectiveAnswer?.trim().length) ||
      currentQuestion?.attachments?.length
    );
  }, [currentQuestion]);

  const hasCreatorAccess = React.useMemo(() => {
    return (
      userId === assignment?.creator ||
      userType === 'creator_completed' ||
      (userType === 'creator_restricted' &&
        checkForPermissions(
          [UAMPermissions.COURSE_ACCESS],
          userAccessPermissions,
        ))
    );
  }, [userId, assignment?.creator, userType, userAccessPermissions]);

  useEffect(() => {
    if (enableCountdown && flow !== 'review' && isOver) {
      submitAnswersAndEndAssignment(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableCountdown, isOver, flow]);

  const handleExit = () => {
    setShowExitModal(false);
    closeModal();
  };

  return (
    <Modal
      title={
        isOver ? undefined : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              paddingLeft: flow === 'review' || showReviewAndSubmit ? 0 : 54, // after close icon
              borderBottom: '1px solid #d9d9d9',
              paddingBottom: 12,
            }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 500,
                textAlign: 'center',
                flex: 1,
              }}>
              {flow === 'review'
                ? 'Review'
                : showReviewAndSubmit
                ? 'Review & Submit'
                : `Question ${currentIndex + 1}/${
                    obtainedMarksForQuestions.length
                  }`}
            </div>
            {flow === 'review' ||
            showReviewAndSubmit ||
            hasCreatorAccess ? null : (
              <div
                style={{
                  marginRight: '12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#666',
                }}>
                Marks: {currentQuestion?.marks || 0}
              </div>
            )}
          </div>
        )
      }
      open={isOpen}
      centered
      bodyStyle={{
        padding: 0,
        borderRadius: 0,
        overflow: 'auto',
      }}
      className="assignmentModal"
      footer={
        !isSaving ? (
          <div className="assignmentFooter">
            {flow !== 'review' && secondsLeft ? (
              <div className="assignment__timer">
                <div className="assignment__timer-info">
                  <TMIcon
                    name="stopwatch-outline"
                    size={24}
                    color={colors.PRIMARY}
                  />
                  <span className="assignment__timer-text">
                    {secondsToHms(secondsLeft)}
                  </span>
                </div>
                <div className="assignment__timer-progress">
                  <CircularProgressBar
                    percentage={ongoingCountdownPercentage}
                    size={24}
                    strokeWidth={4}
                    color={colors.PRIMARY}
                  />
                </div>
              </div>
            ) : null}

            <div className="assignment__actions">
              {isOver ? (
                <div className="assignment__submit-section">
                  <Button
                    className="siteBtn siteBtnPrimary"
                    onClick={closeSubmitAssignmentModal}
                    style={{ width: '100%' }}>
                    {flow === 'preview'
                      ? 'Close'
                      : `Back to ${dynamicItemTitle('course', 'title')}`}
                  </Button>
                </div>
              ) : showReviewAndSubmit ? (
                <div className="assignment__submit-section">
                  <Button
                    onClick={() => submitAnswersAndEndAssignment(false)}
                    className="siteBtn siteBtnPrimary"
                    style={{ width: '100%' }}>
                    Submit
                  </Button>
                </div>
              ) : (
                <>
                  <div className="assignment__action-left">
                    <Button
                      disabled={!hasPrevQuestion}
                      onClick={() => goToPrevOrNextQuestion('prev')}
                      className="siteBtn siteBtnSecondary"
                      style={{ width: '100%' }}>
                      Previous
                    </Button>
                  </div>
                  <div className="assignment__action-right">
                    {flow === 'submit' ? (
                      hasQuestionAnswer ? (
                        <Button
                          disabled={isSaving}
                          onClick={() => {
                            saveQuestionAnswer(
                              currentQuestion?._id || '',
                              'submit',
                              currentQuestion?.type ===
                                AssignmentQuestionCategory.SUBJECTIVE
                                ? {
                                    attachments: currentQuestion?.attachments,
                                    subjectiveAnswer:
                                      currentQuestion?.subjectiveAnswer,
                                  }
                                : {
                                    selectedOptions:
                                      currentQuestion?.selectedOptions,
                                  },
                            );

                            if (hasCreatorAccess) {
                              if (isLastQuestion) {
                                closeSubmitAssignmentModal();
                              } else {
                                goToPrevOrNextQuestion('next');
                              }
                            }
                          }}
                          className="siteBtn siteBtnPrimary"
                          style={{ width: '100%' }}>
                          {isLastQuestion
                            ? hasCreatorAccess
                              ? 'Done'
                              : 'Review & Submit'
                            : hasCreatorAccess
                            ? 'Next'
                            : 'Save & Next'}
                        </Button>
                      ) : (
                        <Button
                          disabled={isSaving}
                          onClick={() => {
                            if (hasCreatorAccess) {
                              if (isLastQuestion) {
                                closeSubmitAssignmentModal();
                              } else {
                                goToPrevOrNextQuestion('next');
                              }
                            } else {
                              goToNextUnattemptedQuestion();
                            }
                          }}
                          className="siteBtn siteBtnPrimary"
                          style={{ width: '100%' }}>
                          Skip
                        </Button>
                      )
                    ) : (
                      <Button
                        disabled={isSaving}
                        onClick={() =>
                          isLastQuestion
                            ? closeSubmitAssignmentModal()
                            : goToPrevOrNextQuestion('next')
                        }
                        className="siteBtn siteBtnPrimary"
                        style={{ width: '100%' }}>
                        {isLastQuestion ? 'Done' : 'Next'}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null
      }
      closable={!isOver}
      destroyOnClose
      onCancel={
        isSaving
          ? undefined
          : () => {
              if (enableCountdown && countdown && flow === 'submit') {
                setShowExitModal(true);
              } else {
                closeSubmitAssignmentModal();
              }
            }
      }
      style={{
        marginBlock: 16,
      }}>
      <div
        className="assignmentModalContent"
        style={{
          height: isOver ? '100%' : 'auto',
        }}>
        <main
          className="assignment__main"
          style={{
            ...(isOver && {
              flex: 1,
              display: 'flex',
            }),
          }}>
          <div
            className="assignment__content"
            style={{
              ...(isOver && {
                flex: 1,
                display: 'flex',
              }),
            }}>
            {isOver ? (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <TMIcon name="stopwatch" size={64} />
                <h2
                  style={{
                    marginBottom: 12,
                    marginTop: 12,
                    fontSize: 24,
                    fontWeight: 600,
                  }}>
                  You're out of time!
                </h2>
                <p style={{ textAlign: 'center', fontSize: 12, color: '#666' }}>
                  Unfortunately, you’ve run out of time to complete the
                  assessment. Your progress has been automatically submitted.{' '}
                  {retaking
                    ? "Don’t worry — you can always retake it when you're ready!"
                    : ''}
                </p>{' '}
              </div>
            ) : currentQuestion ? (
              <div className="assignment__question-wrapper">
                {showReviewAndSubmit ? (
                  <AssignmentReviewSubmit
                    onClickQuestion={(index) => {
                      setCurrentIndex(index);
                      setShowReviewAndSubmit(false);
                    }}
                  />
                ) : (
                  <AssignmentQuestion question={currentQuestion} />
                )}
              </div>
            ) : (
              <div className="assignment__loader">
                <div className="assignment__spinner"></div>
              </div>
            )}
          </div>
        </main>

        {isSaving && (
          <div className="assignment__saving-overlay">
            <div className="assignment__saving-spinner"></div>
          </div>
        )}

        <ConfirmationModal
          open={showExitModal}
          message="Please note that if you exit the assignment, the timer will continue
            running until it expires."
          title="Hold it right there!"
          okayButtonText="Exit Assignment"
          cancelButtonText="Continue"
          handleOk={handleExit}
          handleCancel={() => {
            setShowExitModal(false);
          }}
        />
      </div>
    </Modal>
  );
};

const Assignment: React.FC<Props> = (props) => {
  const { flow, assignment, submission, onProgress, isOpen, closeModal } =
    props;

  return (
    <AssignmentProvider
      isOpen={isOpen}
      closeModal={closeModal}
      flow={flow}
      assignment={assignment}
      submission={submission}
      onProgress={onProgress}>
      <AssignmentBody
        isOpen={isOpen}
        closeModal={closeModal}
        assignment={assignment}
        submission={submission}
        flow={flow}
        onProgress={onProgress}
      />
    </AssignmentProvider>
  );
};

export default Assignment;
